import { useApolloClient, useQuery } from '@apollo/client'
import { UntitledIcon } from '@faceup/icons'
import { ulArrowCircleDownLeft } from '@faceup/icons/ulArrowCircleDownLeft'
import { ulArrowCircleUpRight } from '@faceup/icons/ulArrowCircleUpRight'
import { ulDownload01 } from '@faceup/icons/ulDownload01'
import { ulRefreshCw01 } from '@faceup/icons/ulRefreshCw01'
import { Link, useSearchParams } from '@faceup/router'
import { Card, ContentLayout, twMerge } from '@faceup/ui'
import {
  Button,
  Col,
  DateRangePicker,
  Input,
  InputNumber,
  Modal,
  MultiSelect,
  Row,
  Select,
  Space,
  Switch,
  Table,
  type TableColumns,
  Tag,
  Tooltip,
  Typography,
} from '@faceup/ui-base'
import type { ResultOf } from '@graphql-typed-document-node/core'
import { useDebouncedValue } from '@mantine/hooks'
import moment, { type Moment } from 'moment-timezone'
import { useState } from 'react'
import { graphql } from '../__generated__'
import { InputLabel } from '../Components/InputLabel'
import { IntegrationHttpLogDetail } from '../Components/IntegrationHttpLogs/IntegrationHttpLogDetail'
import {
  downloadLogsAsJsonl,
  formatBytes,
  methodTagColors,
} from '../Components/IntegrationHttpLogs/integrationHttpLogsUtils'

const query = graphql(`
  query IntegrationHttpLogsQuery(
    $page: Int!
    $rowsPerPage: Int!
    $id: String
    $direction: String
    $providers: [String!]
    $statusCategories: [String!]
    $institutionIds: [UUID!]
    $withoutInstitution: Boolean
    $withInstitution: Boolean
    $search: String
    $dateFrom: String
    $dateTo: String
  ) {
    integrationHttpLogs(
      page: $page
      rowsPerPage: $rowsPerPage
      id: $id
      direction: $direction
      providers: $providers
      statusCategories: $statusCategories
      institutionIds: $institutionIds
      withoutInstitution: $withoutInstitution
      withInstitution: $withInstitution
      search: $search
      dateFrom: $dateFrom
      dateTo: $dateTo
    ) {
      totalCount
      items {
        id
        direction
        provider
        method
        url
        institution {
          id
          config {
            id
            institutionName
          }
        }
        statusCode
        durationMs
        requestBodySize
        responseBodySize
        errorSummary
        createdAt
      }
    }
  }
`)

// Its own request so that live mode, which polls the list, does not group the whole table again
const providersQuery = graphql(`
  query IntegrationHttpLogProvidersQuery {
    integrationHttpLogProviders
  }
`)

const exportQuery = graphql(`
  query IntegrationHttpLogsExportQuery(
    $limit: Int!
    $id: String
    $direction: String
    $providers: [String!]
    $statusCategories: [String!]
    $institutionIds: [UUID!]
    $withoutInstitution: Boolean
    $withInstitution: Boolean
    $search: String
    $dateFrom: String
    $dateTo: String
  ) {
    integrationHttpLogsExport(
      limit: $limit
      id: $id
      direction: $direction
      providers: $providers
      statusCategories: $statusCategories
      institutionIds: $institutionIds
      withoutInstitution: $withoutInstitution
      withInstitution: $withInstitution
      search: $search
      dateFrom: $dateFrom
      dateTo: $dateTo
    ) {
      id
      direction
      provider
      method
      url
      institutionId
      institutionName
      institution {
        id
        config {
          id
          institutionName
        }
      }
      statusCode
      durationMs
      requestHeaders
      requestBody
      responseHeaders
      responseBody
      errorMessage
      createdAt
    }
  }
`)

const institutionsQuery = graphql(`
  query IntegrationHttpLogInstitutionsQuery($search: String) {
    integrationHttpLogInstitutions(search: $search) {
      id
      name
    }
  }
`)

type LogItem = ResultOf<typeof query>['integrationHttpLogs']['items'][number]

const PAGE_SIZE = 50

const EXPORT_LIMIT_MAX = 2_000
const EXPORT_LIMIT_DEFAULT = 1_000

// Sentinel select values: logs without a resolved institution (institution_id IS NULL)
// and logs that have one (IS NOT NULL) — neither is a real institution id
const NO_INSTITUTION_VALUE = 'none'
const WITH_INSTITUTION_VALUE = 'defined'

const statusCategoryOptions = [
  { label: '2xx', value: '2xx' },
  { label: '3xx', value: '3xx' },
  { label: '4xx', value: '4xx' },
  { label: '5xx', value: '5xx' },
  { label: 'Error (no response)', value: 'error' },
]

type InstitutionOption = { id: string; name: string | null }

const LIVE_POLL_INTERVAL_MS = 10_000

// 1–2 chars would match nearly every row — wait for a meaningful term
const SEARCH_MIN_LENGTH = 3

export const IntegrationHttpLogsView = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [page, setPage] = useState(0)
  const [direction, setDirection] = useState<string | undefined>()
  const [providers, setProviders] = useState<string[]>([])
  const [statusCategories, setStatusCategories] = useState<string[]>([])
  const [institutionIds, setInstitutionIds] = useState<string[]>([])
  // Institutions picked earlier must keep their labels even after the async options change
  const [selectedInstitutions, setSelectedInstitutions] = useState<InstitutionOption[]>([])
  const [institutionSearch, setInstitutionSearch] = useState('')
  const [search, setSearch] = useState('')
  const [dateRange, setDateRange] = useState<[Moment | null, Moment | null] | null>(null)
  const [isLive, setIsLive] = useState(false)
  // Deep link support: ?id=<logId> filters the table to that single log and expands it
  const [permalinkedId, setPermalinkedId] = useState(() => searchParams.get('id') ?? undefined)

  const [debouncedSearch] = useDebouncedValue(search, 300)
  const [debouncedInstitutionSearch] = useDebouncedValue(institutionSearch, 300)

  const realInstitutionIds = institutionIds.filter(
    id => id !== NO_INSTITUTION_VALUE && id !== WITH_INSTITUTION_VALUE
  )
  const withoutInstitution = institutionIds.includes(NO_INSTITUTION_VALUE)
  const withInstitution = institutionIds.includes(WITH_INSTITUTION_VALUE)

  // Shared by the table query and the export, so the export always matches what is on screen
  const filterVariables = {
    id: permalinkedId,
    direction,
    providers: providers.length ? providers : undefined,
    statusCategories: statusCategories.length ? statusCategories : undefined,
    institutionIds: realInstitutionIds.length ? realInstitutionIds : undefined,
    withoutInstitution: withoutInstitution || undefined,
    withInstitution: withInstitution || undefined,
    search: debouncedSearch.trim().length >= SEARCH_MIN_LENGTH ? debouncedSearch.trim() : undefined,
    dateFrom: dateRange?.[0]?.toISOString(),
    dateTo: dateRange?.[1]?.toISOString(),
  }

  const { data, loading, refetch } = useQuery(query, {
    variables: {
      page,
      rowsPerPage: PAGE_SIZE,
      ...filterVariables,
    },
    pollInterval: isLive ? LIVE_POLL_INTERVAL_MS : 0,
  })

  const { data: providersData } = useQuery(providersQuery)

  const { data: institutionsData, loading: institutionsLoading } = useQuery(institutionsQuery, {
    variables: { search: debouncedInstitutionSearch.trim() || undefined },
  })

  const clearPermalink = () => {
    setPermalinkedId(undefined)
    if (searchParams.get('id')) {
      const params = new URLSearchParams(searchParams)
      params.delete('id')
      setSearchParams(params, { replace: true })
    }
  }

  const refresh = () => {
    setPage(0)
    void refetch({ page: 0 })
  }

  const client = useApolloClient()
  const [isExportModalOpen, setIsExportModalOpen] = useState(false)
  const [exportLimit, setExportLimit] = useState(EXPORT_LIMIT_DEFAULT)
  const [isExporting, setIsExporting] = useState(false)

  const exportLogs = async () => {
    setIsExporting(true)
    try {
      const { data: exportData } = await client.query({
        query: exportQuery,
        variables: { limit: exportLimit, ...filterVariables },
        fetchPolicy: 'no-cache',
      })
      downloadLogsAsJsonl(
        exportData.integrationHttpLogsExport,
        `integration-http-logs-${moment().format('YYYY-MM-DD_HH-mm-ss')}.jsonl`
      )
      setIsExportModalOpen(false)
    } catch {
      // The global Apollo error link already surfaced the failure as a notification
    } finally {
      setIsExporting(false)
    }
  }

  const logs = data?.integrationHttpLogs.items ?? []
  const providerOptions = (providersData?.integrationHttpLogProviders ?? []).map(provider => ({
    label: provider,
    value: provider,
  }))
  const knownInstitutions = [
    ...selectedInstitutions,
    ...(institutionsData?.integrationHttpLogInstitutions ?? []).filter(
      institution => !selectedInstitutions.some(selected => selected.id === institution.id)
    ),
  ]
  const institutionOptions = [
    { label: 'Not defined', value: NO_INSTITUTION_VALUE },
    { label: 'Only with institution', value: WITH_INSTITUTION_VALUE },
    ...knownInstitutions.map(institution => ({
      label: institution.name ?? institution.id,
      value: institution.id,
    })),
  ]

  const columns: TableColumns<LogItem> = [
    {
      title: '',
      key: 'direction',
      width: 220,
      render: record => {
        const isInbound = record.direction === 'inbound'
        const institution = record.institution
        return (
          <div className='flex items-center gap-3'>
            <Tooltip
              title={isInbound ? 'Inbound (webhook to FaceUp)' : 'Outbound (FaceUp calls out)'}
            >
              <span
                role='img'
                aria-label={record.direction}
                className={twMerge(
                  'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                  isInbound
                    ? 'bg-colorInfoBg text-colorInfo'
                    : 'bg-colorSuccessBg text-colorSuccess'
                )}
              >
                <UntitledIcon
                  icon={isInbound ? ulArrowCircleDownLeft : ulArrowCircleUpRight}
                  size={20}
                />
              </span>
            </Tooltip>
            {institution && (
              <Link
                className='min-w-0 truncate'
                to={routes => routes.institution({ id: institution.id })}
              >
                {institution.config.institutionName ?? institution.id}
              </Link>
            )}
          </div>
        )
      },
    },
    {
      title: 'Time',
      key: 'createdAt',
      width: 200,
      render: record => {
        const { requestBodySize, responseBodySize } = record
        const durationClassName =
          record.durationMs >= 10_000
            ? 'text-colorError'
            : record.durationMs >= 2_000
              ? 'text-colorWarning'
              : undefined

        return (
          <div className='flex flex-col'>
            <Tooltip title={moment(record.createdAt).format('YYYY-MM-DD HH:mm:ss')}>
              <span className='whitespace-nowrap'>{moment(record.createdAt).fromNow()}</span>
            </Tooltip>
            <Tooltip title='duration · ↑ request size · ↓ response size'>
              <span className='whitespace-nowrap text-colorTextSecondary text-xs'>
                <span className={durationClassName}>{record.durationMs} ms</span>
                {requestBodySize != null && ` · ↑ ${formatBytes(requestBodySize)}`}
                {responseBodySize != null && ` · ↓ ${formatBytes(responseBodySize)}`}
              </span>
            </Tooltip>
          </div>
        )
      },
    },
    {
      title: 'Request',
      key: 'url',
      render: record => (
        <div className='flex flex-col gap-0.5'>
          <span className='break-all'>
            <Tag color={methodTagColors[record.method] ?? 'default'}>{record.method}</Tag>
            {record.url}
          </span>
          <span className='text-colorTextSecondary text-xs'>{record.provider}</span>
        </div>
      ),
    },
    {
      title: 'Response',
      key: 'statusCode',
      width: 220,
      render: record => {
        const { statusCode, errorSummary } = record

        return (
          <div className='flex flex-col gap-0.5'>
            <div>
              {statusCode == null ? (
                // No response at all — DNS failure, timeout...
                <Tag color='red'>error</Tag>
              ) : (
                <Tag color={statusCode >= 400 ? 'red' : 'green'}>{statusCode}</Tag>
              )}
            </div>
            {errorSummary && (
              <Tooltip title={errorSummary}>
                <span className='block max-w-44 truncate text-colorError text-xs'>
                  {errorSummary}
                </span>
              </Tooltip>
            )}
          </div>
        )
      },
    },
  ]

  return (
    <ContentLayout
      header={
        <ContentLayout.Header
          title='Integration HTTP Logs'
          secondaryContent={
            <Space size='middle'>
              <Space size='small'>
                <Switch checked={isLive} onChange={setIsLive} aria-label='Auto-refresh' />
                <Typography.Text>Live</Typography.Text>
              </Space>
              <Button
                icon={<UntitledIcon icon={ulRefreshCw01} />}
                loading={loading}
                onClick={refresh}
              >
                Refresh
              </Button>
              <Button
                icon={<UntitledIcon icon={ulDownload01} />}
                onClick={() => setIsExportModalOpen(true)}
              >
                Export
              </Button>
            </Space>
          }
        />
      }
    >
      <Space direction='vertical' size='large' className='w-full'>
        <Card>
          <Row gutter={[16, 16]} className='p-6'>
            <Col xs={24} md={4}>
              <InputLabel>Direction</InputLabel>
              <Select
                allowClear
                className='w-full'
                placeholder='All directions'
                value={direction}
                onChange={(value: string | undefined) => {
                  setDirection(value)
                  setPage(0)
                  clearPermalink()
                }}
                options={[
                  { label: 'Inbound (webhook)', value: 'inbound' },
                  { label: 'Outbound (API call)', value: 'outbound' },
                ]}
              />
            </Col>
            <Col xs={24} md={4}>
              <InputLabel>Provider</InputLabel>
              <MultiSelect
                allowClear
                className='w-full'
                placeholder='All providers'
                value={providers}
                onChange={(value: string[]) => {
                  setProviders(value)
                  setPage(0)
                  clearPermalink()
                }}
                options={providerOptions}
              />
            </Col>
            <Col xs={24} md={4}>
              <InputLabel>Status</InputLabel>
              <MultiSelect
                allowClear
                className='w-full'
                placeholder='All statuses'
                value={statusCategories}
                onChange={(value: string[]) => {
                  setStatusCategories(value)
                  setPage(0)
                  clearPermalink()
                }}
                options={statusCategoryOptions}
              />
            </Col>
            <Col xs={24} md={12}>
              <InputLabel>Institution</InputLabel>
              <MultiSelect
                allowClear
                showSearch
                className='w-full'
                placeholder='Search by institution name or id'
                loading={institutionsLoading}
                value={institutionIds}
                filterOption={false}
                onSearch={setInstitutionSearch}
                onChange={(value: string[]) => {
                  setInstitutionIds(value)
                  setSelectedInstitutions(
                    value
                      .filter(id => id !== NO_INSTITUTION_VALUE && id !== WITH_INSTITUTION_VALUE)
                      .map(
                        id =>
                          knownInstitutions.find(institution => institution.id === id) ?? {
                            id,
                            name: null,
                          }
                      )
                  )
                  setInstitutionSearch('')
                  setPage(0)
                  clearPermalink()
                }}
                options={institutionOptions}
                optionRender={option => (
                  <div className='flex flex-col'>
                    <span>{option.label}</span>
                    {option.value !== NO_INSTITUTION_VALUE &&
                      option.value !== WITH_INSTITUTION_VALUE && (
                        <span className='text-colorTextSecondary text-xs'>{option.value}</span>
                      )}
                  </div>
                )}
              />
            </Col>
            <Col xs={24} md={8}>
              <InputLabel>Date range</InputLabel>
              <DateRangePicker
                allowClear
                showTime
                className='w-full'
                value={dateRange}
                onChange={value => {
                  setDateRange(value)
                  setPage(0)
                  clearPermalink()
                }}
              />
            </Col>
            <Col xs={24} md={16}>
              <InputLabel>Search</InputLabel>
              <Input
                allowClear
                placeholder={`Search URL, JSON request, JSON response, error (min ${SEARCH_MIN_LENGTH} characters)`}
                value={search}
                onChange={({ target: { value } }) => {
                  setSearch(value)
                  setPage(0)
                  clearPermalink()
                }}
              />
              {search.trim().length > 0 && search.trim().length < SEARCH_MIN_LENGTH && (
                <Typography.Text type='secondary'>
                  Type at least {SEARCH_MIN_LENGTH} characters to search.
                </Typography.Text>
              )}
            </Col>
          </Row>
        </Card>
        <Table
          shadow
          loading={loading}
          rowKey='id'
          dataSource={logs}
          locale={{ emptyText: ' ' }}
          columns={columns}
          // Fixed layout: column widths come from the column definitions only,
          // so expanding a row cannot reflow them
          tableLayout='fixed'
          expandable={{
            expandRowByClick: true,
            columnWidth: 48,
            defaultExpandedRowKeys: permalinkedId ? [permalinkedId] : [],
            onExpand: (expanded, record) => {
              if (!expanded) {
                // Collapsing also clears the single-log filter from a shared link,
                // so the table falls back to the full listing
                clearPermalink()
                return
              }
              // Keep the URL shareable: it always points at the row currently open
              const params = new URLSearchParams(searchParams)
              params.set('id', record.id)
              setSearchParams(params, { replace: true })
            },
            expandedRowRender: record => <IntegrationHttpLogDetail id={record.id} />,
          }}
          pagination={{
            current: page + 1,
            pageSize: PAGE_SIZE,
            total: data?.integrationHttpLogs.totalCount ?? 0,
            showSizeChanger: false,
          }}
          onChange={pagination => {
            setPage((pagination.current ?? 1) - 1)
          }}
        />
      </Space>
      <Modal
        title='Export logs'
        open={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        footer={
          <div className='flex justify-end gap-2'>
            <Button onClick={() => setIsExportModalOpen(false)}>Cancel</Button>
            <Button type='primary' loading={isExporting} onClick={() => void exportLogs()}>
              Export
            </Button>
          </div>
        }
      >
        <Space direction='vertical' size='middle' className='w-full'>
          <Typography.Text>
            Downloads the logs matching the current filters as a <code>.jsonl</code> file (one JSON
            object per line), newest first.
            {data && ` Currently matching: ${data.integrationHttpLogs.totalCount} logs.`}
          </Typography.Text>
          <div>
            <InputLabel>Limit</InputLabel>
            <InputNumber
              min={1}
              max={EXPORT_LIMIT_MAX}
              step={100}
              className='w-full'
              value={exportLimit}
              onChange={value =>
                setExportLimit(typeof value === 'number' ? value : EXPORT_LIMIT_DEFAULT)
              }
            />
            <Typography.Text type='secondary'>
              Maximum {EXPORT_LIMIT_MAX.toLocaleString()} logs per export.
            </Typography.Text>
          </div>
        </Space>
      </Modal>
    </ContentLayout>
  )
}
